import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import LowStockDialog from './LowStockDialog';
import {
  Inventory as InventoryIcon,
  ShoppingCart as SalesIcon,
  TrendingUp as ProfitIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as DiscountIcon,
  TrendingDown as LossIcon,
  Home as HomeIcon,
  AccountBalanceWallet as CostIcon
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { inventoryService } from '../services/InventoryService';

const COLORS = ['#E53935', '#FF1744', '#4CAF50', '#FFC107', '#7C4DFF', '#FF5722', '#00BCD4', '#8BC34A'];

// ── Stat Card Component ──
const StatCard = ({ icon: Icon, label, value, subtitle, color, gradient, onClick, clickable }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      background: '#2D2D2D',
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeft: `4px solid ${color}`,
      cursor: clickable ? 'pointer' : 'default',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': clickable ? {
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.4)',
      } : {
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, right: 0,
        width: 80, height: 80,
        borderRadius: '50%',
        background: `${color}15`,
        transform: 'translate(20px, -20px)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box sx={{
        width: { xs: 28, sm: 34 }, height: { xs: 28, sm: 34 },
        borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}26`,
      }}>
        <Icon sx={{ fontSize: { xs: 16, sm: 18 }, color: color }} />
      </Box>
      <Typography sx={{
        fontSize: { xs: '0.75rem', sm: '0.75rem' },
        fontWeight: 600, color: '#BDBDBD',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontWeight: 800, color: '#F5F5F5',
      fontSize: { xs: '1.1rem', sm: '1.5rem' },
      letterSpacing: '-0.02em', lineHeight: 1.2,
      mb: 0.5,
      overflow: 'hidden', textOverflow: 'ellipsis',
    }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.7rem' }, color: '#BDBDBD', fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ── Chart Wrapper ──
const ChartCard = ({ title, children, height = 280 }) => (
  <Box sx={{
    borderRadius: 3, p: { xs: 1.5, sm: 3 },
    background: '#2D2D2D',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'box-shadow 0.2s ease',
    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.3)' },
    minHeight: { xs: 200, sm: 'auto' },
  }}>
    <Typography sx={{
      fontSize: { xs: '0.85rem', sm: '1rem' },
      fontWeight: 700, color: '#F5F5F5',
      mb: 2.5, letterSpacing: '-0.01em',
    }}>
      {title}
    </Typography>
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  </Box>
);

// ── Custom Tooltip ──
const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload) return null;
  return (
    <Box sx={{
      background: '#3A3A3A', backdropFilter: 'blur(10px)',
      borderRadius: 2, p: 1.5, minWidth: 140,
      border: '1px solid rgba(255,255,255,0.1)',
    }}>
      <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD', mb: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      {payload.map((entry, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: entry.color }} />
            <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD' }}>{entry.name}</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#F5F5F5', fontWeight: 700 }}>
            {formatCurrency(entry.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const HomeDashboard = ({ shopName }) => {
  const [stats, setStats] = useState({
    totalItems: 0, totalValue: 0, totalSales: 0,
    totalCosts: 0, totalDiscount: 0, totalLoss: 0,
    estimatedProfit: 0, lowStockItems: 0, totalItemsCount: 0
  });
  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lowStockDialogOpen, setLowStockDialogOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState('monthly');

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const items = await inventoryService.getAllItems(shopName);
      const lowStockItems = items.filter(item => {
        const qty = Number(item.quantity) || 0;
        return qty > 0 && qty <= 5;
      });
      const allActivities = await inventoryService.getAllActivities(shopName);

      const totalValue = items.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const cost = Number(item.originalCost) || 0;
        return sum + (qty * cost);
      }, 0);

      const now = new Date();
      let startDate = new Date();

      switch (timePeriod) {
        case 'daily':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'weekly':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarterly':
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
          break;
        case 'annually':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      const soldActivities = allActivities.filter(a => {
        if (a.action !== 'SOLD') return false;
        const activityDate = new Date(a.timestamp);
        return activityDate.getTime() >= startDate.getTime();
      });

      let totalSales = 0;
      let totalDiscount = 0;
      let totalCosts = 0;
      let totalLoss = 0;
      let netProfit = 0;
      const salesByDate = {};

      soldActivities.forEach(activity => {
        let quantity = 0;
        let discount = 0;

        const quantityMatch = activity.details?.match(/Sold\s+(\d+)\s+units/);
        if (quantityMatch) {
          quantity = parseFloat(quantityMatch[1]) || 0;
        }

        if (activity.discountAmount !== undefined && activity.discountAmount !== null) {
          const discountValue = activity.discountAmount;
          discount = typeof discountValue === 'number' ? discountValue : parseFloat(discountValue) || 0;
        }

        if (discount === 0 && activity.details) {
          let discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)\s+total/i);
          if (discountMatch && discountMatch[1]) {
            discount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
          } else {
            discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)/i);
            if (discountMatch && discountMatch[1]) {
              discount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
            }
          }
        }

        let saleAmount = 0;
        const finalMatch = activity.details?.match(/Final:\s*₱([\d,]+\.?\d*)/i);
        if (finalMatch && finalMatch[1]) {
          saleAmount = parseFloat(finalMatch[1].replace(/,/g, '')) || 0;
        } else {
          const amountMatch = activity.details?.match(/₱([\d,]+\.?\d*)/);
          if (amountMatch) {
            let amountStr = amountMatch[1].replace(/,/g, '');
            if (amountStr.includes(' ')) {
              amountStr = amountStr.split(' ')[0];
            }
            saleAmount = parseFloat(amountStr) || 0;
          }
        }

        totalSales += saleAmount;
        totalDiscount += discount;

        let itemOriginalCost = parseFloat(activity.itemCost) || 0;

        if (itemOriginalCost === 0) {
          let item = items.find(i => i.name === activity.itemName && i.brand === activity.itemBrand);
          if (!item) { item = items.find(i => i.name === activity.itemName); }
          itemOriginalCost = item ? (parseFloat(item.originalCost) || 0) : 0;
        }

        const costOfGoodsSold = quantity * itemOriginalCost;
        totalCosts += costOfGoodsSold;

        const transactionLoss = Math.max(0, costOfGoodsSold - saleAmount);
        totalLoss += transactionLoss;

        const date = new Date(activity.timestamp);
        let dateKey;
        if (timePeriod === 'daily') {
          const hour = date.getHours();
          dateKey = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${hour}:00`;
        } else if (timePeriod === 'weekly' || timePeriod === 'monthly') {
          dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else {
          dateKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }

        if (!salesByDate[dateKey]) {
          salesByDate[dateKey] = { sales: 0, discount: 0, loss: 0 };
        }
        salesByDate[dateKey].sales += saleAmount;
        salesByDate[dateKey].discount += discount;
        salesByDate[dateKey].loss += transactionLoss;
      });

      let salesChartData = Object.entries(salesByDate)
        .map(([date, data]) => ({ date, sales: data.sales || 0, discount: data.discount || 0, loss: data.loss || 0 }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      if (timePeriod === 'daily') salesChartData = salesChartData.slice(-24);
      else if (timePeriod === 'weekly') salesChartData = salesChartData.slice(-7);
      else if (timePeriod === 'monthly') salesChartData = salesChartData.slice(-30);
      else if (timePeriod === 'quarterly') salesChartData = salesChartData.slice(-3);
      else salesChartData = salesChartData.slice(-12);

      const categoryCount = {};
      items.forEach(item => {
        const category = item.category || 'Other';
        if (!categoryCount[category]) {
          categoryCount[category] = { name: category, value: 0, sales: 0 };
        }
        categoryCount[category].value += Number(item.quantity) || 0;
      });

      const categorySales = {};
      soldActivities.forEach(activity => {
        const category = activity.itemCategory || 'Other';
        if (!categorySales[category]) { categorySales[category] = 0; }
        const match = activity.details?.match(/₱([\d,]+\.?\d*)/);
        if (match) {
          const amount = parseFloat(match[1].replace(/,/g, ''));
          if (!isNaN(amount)) { categorySales[category] += amount; }
        }
      });

      Object.keys(categoryCount).forEach(category => {
        categoryCount[category].sales = categorySales[category] || 0;
      });

      const categoryChartData = Object.values(categoryCount);

      const periodSalesData = {};

      if (timePeriod === 'daily') {
        for (let i = 23; i >= 0; i--) {
          const date = new Date(now.getTime() - i * 60 * 60 * 1000);
          const hour = date.getHours();
          const hourKey = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${hour}:00`;
          periodSalesData[hourKey] = { sales: 0, discount: 0, loss: 0 };
        }
      } else if (timePeriod === 'weekly') {
        for (let i = 6; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          const dayKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          periodSalesData[dayKey] = { sales: 0, discount: 0, loss: 0 };
        }
      } else if (timePeriod === 'monthly') {
        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          periodSalesData[monthKey] = { sales: 0, discount: 0, loss: 0 };
        }
      } else if (timePeriod === 'quarterly') {
        for (let i = 3; i >= 0; i--) {
          const qtr = Math.floor((now.getMonth() - i * 3) / 3);
          const year = now.getFullYear() - Math.floor((now.getMonth() - i * 3) / 12);
          const quarterKey = `Q${qtr + 1} ${year}`;
          periodSalesData[quarterKey] = { sales: 0, discount: 0, loss: 0 };
        }
      } else {
        for (let i = 4; i >= 0; i--) {
          const year = now.getFullYear() - i;
          periodSalesData[year.toString()] = { sales: 0, discount: 0, loss: 0 };
        }
      }

      soldActivities.forEach(activity => {
        const date = new Date(activity.timestamp);
        let periodKey;

        if (timePeriod === 'daily') {
          const hour = date.getHours();
          periodKey = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${hour}:00`;
        } else if (timePeriod === 'weekly') {
          periodKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (timePeriod === 'monthly') {
          periodKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        } else if (timePeriod === 'quarterly') {
          const qtr = Math.floor(date.getMonth() / 3) + 1;
          periodKey = `Q${qtr} ${date.getFullYear()}`;
        } else {
          periodKey = date.getFullYear().toString();
        }

        if (periodSalesData.hasOwnProperty(periodKey)) {
          let periodSaleAmount = 0;
          let periodDiscount = 0;
          let periodQuantity = 0;

          if (activity.discountAmount !== undefined && activity.discountAmount !== null) {
            const discountValue = activity.discountAmount;
            periodDiscount = typeof discountValue === 'number' ? discountValue : parseFloat(discountValue) || 0;
          }

          if (periodDiscount === 0 && activity.details) {
            let discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)\s+total/i);
            if (discountMatch && discountMatch[1]) {
              periodDiscount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
            } else {
              discountMatch = activity.details.match(/Discount:\s*₱([\d,]+\.?\d*)/i);
              if (discountMatch && discountMatch[1]) {
                periodDiscount = parseFloat(discountMatch[1].replace(/,/g, '')) || 0;
              }
            }
          }

          const finalMatch = activity.details?.match(/Final:\s*₱([\d,]+\.?\d*)/i);
          if (finalMatch && finalMatch[1]) {
            periodSaleAmount = parseFloat(finalMatch[1].replace(/,/g, '')) || 0;
          } else {
            const amountMatch = activity.details?.match(/₱([\d,]+\.?\d*)/);
            if (amountMatch) {
              const baseAmount = parseFloat(amountMatch[1].replace(/,/g, '')) || 0;
              if (periodDiscount > 0 && baseAmount > periodDiscount) {
                periodSaleAmount = baseAmount - periodDiscount;
              } else {
                periodSaleAmount = baseAmount;
              }
            }
          }

          const quantityMatch = activity.details?.match(/Sold\s+(\d+)\s+units/);
          if (quantityMatch) {
            periodQuantity = parseFloat(quantityMatch[1]) || 0;
          }

          let periodItemCost = parseFloat(activity.itemCost) || 0;
          if (periodItemCost === 0) {
            let item = items.find(i => i.name === activity.itemName && i.brand === activity.itemBrand);
            if (!item) { item = items.find(i => i.name === activity.itemName); }
            periodItemCost = item ? (parseFloat(item.originalCost) || 0) : 0;
          }

          const costOfGoodsSold = periodQuantity * periodItemCost;
          const transactionLoss = Math.max(0, costOfGoodsSold - periodSaleAmount);

          periodSalesData[periodKey].sales += periodSaleAmount;
          periodSalesData[periodKey].discount += periodDiscount;
          periodSalesData[periodKey].loss += transactionLoss;
        }
      });

      const monthlyChartData = Object.entries(periodSalesData).map(([period, data]) => ({
        month: period, sales: data.sales, discount: data.discount, loss: data.loss
      }));

      netProfit = totalSales - totalCosts;

      setStats({
        totalItems: items.length, totalValue, totalSales, totalCosts,
        totalDiscount, totalLoss,
        estimatedProfit: netProfit,
        lowStockItems: lowStockItems.length,
        totalItemsCount: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
      });

      setSalesData(salesChartData);
      setCategoryData(categoryChartData);
      setMonthlySales(monthlyChartData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [shopName, timePeriod]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency', currency: 'PHP'
    }).format(amount);
  };

  const getPeriodLabel = () => {
    switch (timePeriod) {
      case 'daily': return 'Today';
      case 'weekly': return 'Last 7 Days';
      case 'monthly': return 'This Month';
      case 'quarterly': return 'This Quarter';
      case 'annually': return 'This Year';
      default: return 'This Month';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <HomeIcon sx={{ fontSize: 28, color: '#F5F5F5' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#F5F5F5' }}>
            Loading dashboard...
          </Typography>
        </Box>
        <LinearProgress sx={{ borderRadius: 2, height: 4 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 3, sm: 4 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5,
            background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(229,57,53,0.3)',
          }}>
            <HomeIcon sx={{ fontSize: 22, color: 'white' }} />
          </Box>
          <Box>
            <Typography sx={{
              fontWeight: 800, color: '#F5F5F5',
              fontSize: { xs: '1.15rem', sm: '1.5rem' },
              letterSpacing: '-0.02em',
            }}>
              Dashboard
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#BDBDBD', fontWeight: 500 }}>
              {shopName} • {getPeriodLabel()}
            </Typography>
          </Box>
        </Box>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, '& .MuiSelect-select': { fontSize: { xs: '16px', sm: '0.875rem' } } }}>
          <InputLabel sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Time Period</InputLabel>
          <Select
            value={timePeriod}
            label="Time Period"
            onChange={(e) => setTimePeriod(e.target.value)}
          >
            <MenuItem value="daily" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Daily</MenuItem>
            <MenuItem value="weekly" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Weekly</MenuItem>
            <MenuItem value="monthly" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Monthly</MenuItem>
            <MenuItem value="quarterly" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Quarterly</MenuItem>
            <MenuItem value="annually" sx={{ fontSize: { xs: '16px', sm: '0.875rem' } }}>Annually</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* ── Stat Cards ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: { xs: 1, sm: 2 },
        mb: { xs: 3, sm: 4 },
      }}>
        <StatCard icon={InventoryIcon} label="Total Items" value={stats.totalItems}
          subtitle={`${stats.totalItemsCount} units in stock`} color="#E53935" />
        <StatCard icon={MoneyIcon} label="Inventory Value" value={formatCurrency(stats.totalValue)}
          color="#FF1744" />
        <StatCard icon={SalesIcon} label="Total Sales" value={formatCurrency(stats.totalSales)}
          color="#4CAF50" />
        <StatCard icon={CostIcon} label="Total Costs" value={formatCurrency(stats.totalCosts)}
          color="#7C4DFF" />
        <StatCard icon={DiscountIcon} label="Discount" value={formatCurrency(stats.totalDiscount)}
          color="#FFC107" />
        <StatCard icon={LossIcon} label="Total Loss" value={formatCurrency(stats.totalLoss)}
          color="#FF5252" />
        <StatCard icon={ProfitIcon} label="Net Profit" value={formatCurrency(stats.estimatedProfit)}
          subtitle="Sales − Costs"
          color={stats.estimatedProfit >= 0 ? '#4CAF50' : '#FF5252'} />
        <StatCard icon={WarningIcon} label="Low Stock" value={stats.lowStockItems}
          subtitle={stats.lowStockItems > 0 ? 'Click to view & edit' : 'All stocked'}
          color="#FFC107"
          clickable={stats.lowStockItems > 0}
          onClick={() => stats.lowStockItems > 0 && setLowStockDialogOpen(true)} />
      </Box>

      {/* ── Charts ── */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Sales Trend */}
        <Grid item xs={12} lg={8}>
          <ChartCard title={`Sales Trend — ${getPeriodLabel()}`} height={280}>
            <AreaChart data={monthlySales}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E53935" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#E53935" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="sales" stroke="#E53935" strokeWidth={2.5} fill="url(#salesGrad)" name="Sales" dot={false} />
              <Area type="monotone" dataKey="discount" stroke="#FFC107" strokeWidth={1.5} fill="transparent" name="Discount" dot={false} strokeDasharray="4 4" />
              <Area type="monotone" dataKey="loss" stroke="#FF5252" strokeWidth={1.5} fill="transparent" name="Loss" dot={false} strokeDasharray="4 4" />
            </AreaChart>
          </ChartCard>
        </Grid>

        {/* Category Pie */}
        <Grid item xs={12} lg={4}>
          <ChartCard title="Inventory by Category" height={280}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: '#BDBDBD', strokeWidth: 1 }}
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                return (
                  <Box sx={{
                    background: '#3A3A3A', backdropFilter: 'blur(10px)',
                    borderRadius: 2, p: 1.5, border: '1px solid rgba(255,255,255,0.1)',
                  }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#F5F5F5', fontWeight: 700 }}>
                      {payload[0].name}: {payload[0].value} units
                    </Typography>
                  </Box>
                );
              }} />
            </PieChart>
          </ChartCard>
        </Grid>

        {/* Sales Breakdown Bar */}
        <Grid item xs={12} md={6}>
          <ChartCard title={`Sales Breakdown — ${getPeriodLabel()}`} height={260}>
            <BarChart data={salesData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" fill="#E53935" name="Sales" radius={[4, 4, 0, 0]} />
              <Bar dataKey="discount" fill="#FFC107" name="Discount" radius={[4, 4, 0, 0]} />
              <Bar dataKey="loss" fill="#FF5252" name="Loss" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>
        </Grid>

        {/* Sales by Category */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Sales by Category" height={260}>
            <BarChart data={categoryData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#BDBDBD' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
              <Bar dataKey="sales" fill="#E53935" name="Sales Amount" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Low Stock Dialog */}
      <LowStockDialog
        open={lowStockDialogOpen}
        onClose={() => setLowStockDialogOpen(false)}
        shopName={shopName}
        onUpdate={loadDashboardData}
      />
    </Box>
  );
};

export default HomeDashboard;
